/**
 * HouseholdSwitcher — UX §3.14. Dropdown showing memberships + "Create new".
 * Active household marked with a small accent dot. Trigger uses serif italic.
 *
 * Wires `useMemberships` + `useSwitchHousehold` for full behavior.
 */
import { CaretDown, Plus } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
} from './DropdownMenu';
import { cn } from '../../lib/cn';
import type { HouseholdId } from '@shared/types';
import { useMemberships } from '../../lib/queries';
import { useSwitchHousehold } from '../../lib/household';
import { useAppStore } from '../../lib/store';

export interface HouseholdSwitcherProps {
  className?: string;
}

export function HouseholdSwitcher({ className }: HouseholdSwitcherProps) {
  const memberships = useMemberships();
  const active = useAppStore((s) => s.activeHouseholdId);
  const switcher = useSwitchHousehold();
  const navigate = useNavigate();

  const open = useAppStore((s) => s.householdSwitcherOpen);
  const setOpen = (v: boolean) =>
    v
      ? useAppStore.getState().openHouseholdSwitcher()
      : useAppStore.getState().closeHouseholdSwitcher();

  const list = memberships.data?.memberships ?? [];
  const currentName =
    list.find((m) => m.household_id === active)?.household_name ?? 'No household';

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center gap-1.5',
            'font-serif italic text-base text-ink-900',
            'hover:text-ink-700 transition-colors duration-200 ease-out-slow',
            'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent',
            'px-1 py-0.5',
            className,
          )}
          aria-label="Switch household"
        >
          <span className="max-w-[14rem] truncate">{currentName}</span>
          <CaretDown size={14} weight="regular" aria-hidden />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="start" sideOffset={6}>
        {list.map((m) => {
          const isActive = m.household_id === active;
          return (
            <DropdownMenu.Item
              key={m.household_id}
              onSelect={() => {
                if (!isActive && !switcher.isPending) {
                  void switcher.switchTo(m.household_id as HouseholdId);
                }
              }}
              className="justify-between"
            >
              <span className="flex items-center gap-2">
                <span
                  aria-hidden
                  className={cn(
                    'h-1.5 w-1.5 rounded-full',
                    isActive ? 'bg-accent' : 'bg-transparent',
                  )}
                />
                <span className={cn('font-serif italic', isActive && 'text-ink-900')}>
                  {m.household_name}
                </span>
              </span>
              <span className="text-[0.65rem] uppercase tracking-wider text-ink-500">
                {m.role}
              </span>
            </DropdownMenu.Item>
          );
        })}
        <DropdownMenu.Separator />
        <DropdownMenu.Item
          onSelect={() => navigate('/onboard/create-household')}
        >
          <Plus size={14} weight="regular" aria-hidden />
          <span>Create new household</span>
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}
